import type { ClientEntity } from '../models/entity/client.entity';
import { displayExternalId } from './external-id';

/**
 * PD-17381 — the customer number a customer knows their account by: the
 * client's `externalId` (the ERP's customer number, e.g. "UT-10386"), falling
 * back to our internal id when the client has none.
 *
 * Statements have printed this as their "Customer ID" since PD-14041, so
 * every surface that shows a customer number next to a statement (the CRM
 * and storefront statement lists, the storefront account pickers, and a
 * statement template's address block with Show customer number on) uses
 * this one rule and the numbers always match the box on the PDF.
 *
 * `externalId2` is deliberately NOT consulted, unlike `displayExternalId`'s
 * full chain: the statement's Customer ID has always been `externalId`, and a
 * second rule here would let the lists disagree with the documents.
 */
export function clientCustomerNumber(
	client: Pick<ClientEntity, 'id' | 'externalId'>,
): string {
	return (
		displayExternalId({ id: client.id, externalId: client.externalId }) ??
		String(client.id)
	);
}

/**
 * Whether a name already begins with the customer number, so a caller that
 * puts the number in front of the name does not print it twice. Some tenants
 * key the number into the account name itself ("UT-10386 Tokyo Teriyaki").
 *
 * The number must be followed by the end of the name or by a character that
 * is not a letter or digit, so "103" is not mistaken for the start of
 * "10386 Main St". Case-insensitive, since ERPs are inconsistent about it.
 */
export function nameStartsWithCustomerNumber(
	name: string,
	customerNumber: string,
): boolean {
	const trimmedName = name.trim().toLowerCase();
	const trimmedNumber = customerNumber.trim().toLowerCase();
	if (trimmedNumber === '' || !trimmedName.startsWith(trimmedNumber)) {
		return false;
	}
	const next = trimmedName.charAt(trimmedNumber.length);
	return next === '' || !/[\p{L}\p{N}]/u.test(next);
}

/**
 * The name with the customer number in front of it ("UT-10386 Tokyo
 * Teriyaki"), as a statement template's address block prints it when its
 * Show customer number option is on. Returns the name
 * unchanged when there is no number or the name already starts with it, and
 * the number alone when there is no name.
 */
export function prefixCustomerNumber(
	name: string,
	customerNumber: string | null | undefined,
): string {
	const trimmedNumber = customerNumber?.trim() ?? '';
	if (trimmedNumber === '') {
		return name;
	}
	if (name.trim() === '') {
		return trimmedNumber;
	}
	if (nameStartsWithCustomerNumber(name, trimmedNumber)) {
		return name;
	}
	return `${trimmedNumber} ${name}`;
}
